import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { apiFetch } from '../lib/api';

const useEmailDrafts = ({
  userId,
  fullName,
  userBio,
  showToast,
  checkFreemiumLimit,
  incrementUsage,
  ensureUserRecordExists,
}) => {
  const [drafts, setDrafts] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedAlumniForEmail, setSelectedAlumniForEmail] = useState(null);
  const [loadingDraftId, setLoadingDraftId] = useState(null);
  const [sendingDraftId, setSendingDraftId] = useState(null);
  const [refiningDraftId, setRefiningDraftId] = useState(null);
  const [showRefineInput, setShowRefineInput] = useState({});
  const [refineInstructions, setRefineInstructions] = useState({});

  const openEmailModal = useCallback(alumni => {
    setSelectedAlumniForEmail(alumni);
    setShowEmailModal(true);
  }, []);

  const closeEmailModal = useCallback(() => {
    setShowEmailModal(false);
    setSelectedAlumniForEmail(null);
  }, []);

  const handleSubjectChange = useCallback((id, value) => {
    setDrafts(prev => ({ ...prev, [`${id}_subject`]: value }));
  }, []);

  const handleBodyChange = useCallback((id, value) => {
    setDrafts(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleSubjectBlur = useCallback(async (row, value) => {
    await supabase.from('saved_alumni').update({ email_subject: value }).eq('id', row.id);
  }, []);

  const handleBodyBlur = useCallback(async (row, value) => {
    await supabase.from('saved_alumni').update({ email_body: value }).eq('id', row.id);
  }, []);

  const generateDraft = useCallback(
    async row => {
      if (!checkFreemiumLimit('emails_drafted')) {
        return;
      }
      setLoadingDraftId(row.id);
      try {
        const response = await apiFetch(`/generate_draft_email?user_id=${userId || ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_name: fullName,
            student_bio: userBio,
            alumni_name: row.alumni_name,
            alumni_position: row.alumni_position,
            alumni_company: row.alumni_company,
          }),
        });
        const data = await response.json();
        if (response.status === 429) {
          showToast(data.detail?.error || 'You have reached your daily email draft limit. Please try again tomorrow.', 'error');
          return;
        }
        setDrafts(prev => ({
          ...prev,
          [row.id]: data.body,
          [`${row.id}_subject`]: data.subject,
        }));
        await supabase.from('saved_alumni').update({ email_subject: data.subject, email_body: data.body }).eq('id', row.id);
        if (userId) {
          const exists = await ensureUserRecordExists(userId);
          if (exists) {
            await incrementUsage('emails_drafted');
          }
        }
      } catch (error) {
        showToast('Failed to generate draft email.', 'error');
      }
      setLoadingDraftId(null);
    },
    [checkFreemiumLimit, ensureUserRecordExists, fullName, incrementUsage, showToast, userBio, userId],
  );

  const refineEmail = useCallback(
    async row => {
      setRefiningDraftId(row.id);
      try {
        const currentSubject = drafts[`${row.id}_subject`] ?? row.email_subject ?? '';
        const currentBody = drafts[row.id] ?? row.email_body ?? '';
        const instructions = refineInstructions[row.id] || '';
        const response = await apiFetch(`/refine_email?user_id=${userId || ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_name: fullName,
            student_bio: userBio,
            alumni_name: row.alumni_name,
            alumni_position: row.alumni_position,
            alumni_company: row.alumni_company,
            current_subject: currentSubject,
            current_body: currentBody,
            refinement_instructions: instructions,
          }),
        });
        const data = await response.json();
        if (response.status === 429) {
          showToast(data.detail?.error || 'You have reached your daily email draft limit. Please try again tomorrow.', 'error');
          return;
        }
        setDrafts(prev => ({
          ...prev,
          [row.id]: data.body,
          [`${row.id}_subject`]: data.subject,
        }));
        await supabase.from('saved_alumni').update({ email_subject: data.subject, email_body: data.body }).eq('id', row.id);
        setShowRefineInput(prev => ({ ...prev, [row.id]: false }));
        setRefineInstructions(prev => ({ ...prev, [row.id]: '' }));
      } catch (error) {
        showToast('Failed to refine email.', 'error');
      }
      setRefiningDraftId(null);
    },
    [drafts, fullName, refineInstructions, showToast, userBio, userId],
  );

  const sendEmail = useCallback(
    async row => {
      if (!checkFreemiumLimit('emails_sent')) {
        return;
      }
      setSendingDraftId(row.id);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.provider_token;
        if (!accessToken) {
          showToast('Email sending requires Google authentication. Please sign in with Google to send emails.', 'warning');
          setSendingDraftId(null);
          return;
        }
        const subject = drafts[`${row.id}_subject`] ?? row.email_subject ?? '';
        const body = drafts[row.id] ?? row.email_body ?? '';
        const response = await apiFetch(`/send_email?user_id=${userId || ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_token: accessToken,
            to: row.alumni_email,
            subject,
            body,
          }),
        });
        const data = await response.json();
        if (response.status === 429) {
          showToast(data.detail?.error || 'You have reached your daily email sending limit. Please try again tomorrow.', 'error');
          return;
        }
        if (response.ok) {
          showToast('Email sent!', 'success');
          const now = new Date().toISOString();
          await supabase.from('saved_alumni').update({ sent_at: now, was_sent: true }).eq('id', row.id);
          // Note: refreshSavedAlumni now requires chatId parameter
          // This will be handled by the parent component when chat changes
          // if (refreshSavedAlumni) {
          //   await refreshSavedAlumni(chatId);
          // }
          if (userId) {
            const exists = await ensureUserRecordExists(userId);
            if (exists) {
              await incrementUsage('emails_sent');
            }
          }
        } else {
          showToast('Failed to send email.', 'error');
        }
      } catch (error) {
        showToast('Failed to send email.', 'error');
      }
      setSendingDraftId(null);
    },
    [checkFreemiumLimit, drafts, ensureUserRecordExists, incrementUsage, showToast, userId],
  );

  const resetEmailDrafts = useCallback(() => {
    setDrafts({});
    setShowEmailModal(false);
    setSelectedAlumniForEmail(null);
    setLoadingDraftId(null);
    setSendingDraftId(null);
    setRefiningDraftId(null);
    setShowRefineInput({});
    setRefineInstructions({});
  }, []);

  return {
    drafts,
    showEmailModal,
    selectedAlumniForEmail,
    loadingDraftId,
    sendingDraftId,
    refiningDraftId,
    showRefineInput,
    refineInstructions,
    openEmailModal,
    closeEmailModal,
    setShowRefineInput,
    setRefineInstructions,
    handleSubjectChange,
    handleBodyChange,
    handleSubjectBlur,
    handleBodyBlur,
    generateDraft,
    refineEmail,
    sendEmail,
    resetEmailDrafts,
  };
};

export { useEmailDrafts };
